function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, getOrThrow } from '@deephaven/utils';
import CellRenderer from "./CellRenderer.js";
import { isExpandableGridModel } from "./ExpandableGridModel.js";
import GridUtils from "./GridUtils.js";
import memoizeClear from "./memoizeClear.js";
class TextCellRenderer extends CellRenderer {
  constructor() {
    super(...arguments);
    /**
     * Returns an array of token boxes with the coordinates relative to the top left corner of the text
     */
    _defineProperty(this, "getCachedTokenBoxesForVisibleCell", memoizeClear((truncatedText, tokens, _font, _baseline, textHeight, context) => {
      var top = 0;
      var bottom = textHeight;
      var tokenBoxes = [];

      // The index where the last token ended
      var lastTokenEnd = 0;
      // The width of the text preceding the current token
      var currentTextWidth = 0;
      // Loop through array and push them to array
      for (var i = 0; i < tokens.length; i += 1) {
        var token = tokens[i];
        var start = token.start,
          end = token.end;
        // The last token value is calculated based on the full text so the value needs to be truncated
        var value = end > truncatedText.length ? truncatedText.substring(start) : token.value;

        // Add the width of the text in between this token and the last token
        currentTextWidth += context.measureText(truncatedText.substring(lastTokenEnd, start)).width;
        var tokenWidth = context.measureText(value).width;

        // Check if the x position is less than the grid x, then tokenWidth should be shifted by gridX - startX

        var left = currentTextWidth;
        var right = left + tokenWidth;
        var newTokenBox = {
          x1: left,
          y1: top,
          x2: right,
          y2: bottom,
          token
        };
        tokenBoxes.push(newTokenBox);
        lastTokenEnd = end;
        currentTextWidth += tokenWidth;
      }
      return tokenBoxes;
    }, {
      max: 10000
    }));
  }
  drawCellContent(context, state, column, row) {
    var metrics = state.metrics,
      model = state.model,
      theme = state.theme;
    var fontWidthsLower = metrics.fontWidthsLower,
      fontWidthsUpper = metrics.fontWidthsUpper,
      modelColumns = metrics.modelColumns,
      modelRows = metrics.modelRows,
      allRowHeights = metrics.allRowHeights,
      firstColumn = metrics.firstColumn;
    var isFirstColumn = column === firstColumn;
    var textColor = theme.textColor;
    var rowHeight = getOrThrow(allRowHeights, row);
    var modelRow = getOrThrow(modelRows, row);
    var modelColumn = getOrThrow(modelColumns, column);
    var text = model.textForCell(modelColumn, modelRow);
    var truncationChar = model.truncationCharForCell(modelColumn, modelRow);
    if (text && rowHeight > 0) {
      var textAlign = model.textAlignForCell(modelColumn, modelRow) || 'left';
      context.textAlign = textAlign;
      var color = model.colorForCell(modelColumn, modelRow, theme) || textColor;
      context.fillStyle = color;
      context.save();
      var _GridUtils$getTextRen = GridUtils.getTextRenderMetrics(state, column, row),
        textWidth = _GridUtils$getTextRen.width,
        textX = _GridUtils$getTextRen.x,
        textY = _GridUtils$getTextRen.y;
      var fontWidthLower = fontWidthsLower.get(context.font);
      var fontWidthUpper = fontWidthsUpper.get(context.font);
      var truncatedText = this.getCachedTruncatedString(context, text, textWidth, fontWidthLower, fontWidthUpper, truncationChar);
      var tokens = model.tokensForCell(modelColumn, modelRow, truncatedText.length);
      if (truncatedText) {
        var tokenIndex = 0;
        var textStart = 0;
        var left = textX;
        var _context$measureText = context.measureText(truncatedText),
          actualBoundingBoxDescent = _context$measureText.actualBoundingBoxDescent;
        while (textStart < truncatedText.length) {
          var _ref, _token$end;
          var nextToken = tokens[tokenIndex];
          var token = textStart === (nextToken === null || nextToken === void 0 ? void 0 : nextToken.start) ? nextToken : null;
          var textEnd = (_ref = (_token$end = token === null || token === void 0 ? void 0 : token.end) !== null && _token$end !== void 0 ? _token$end : nextToken === null || nextToken === void 0 ? void 0 : nextToken.start) !== null && _ref !== void 0 ? _ref : truncatedText.length;
          var value = truncatedText.substring(textStart, textEnd);
          var _context$measureText2 = context.measureText(value),
            width = _context$measureText2.width;
          var widthOfUnderline = value.endsWith('…') ? width - context.measureText('…').width : width;

          // Set the styling based on the token, then draw the text
          if (token != null) {
            context.fillStyle = theme.hyperlinkColor;
            context.fillText(value, left, textY);
            context.fillRect(left, textY + actualBoundingBoxDescent, widthOfUnderline, 1);
          } else {
            context.fillStyle = color;
            context.fillText(value, left, textY);
          }
          left += width;
          textStart = textEnd;
          if (token != null) tokenIndex += 1;
        }
      }
      context.restore();
    }
    if (isFirstColumn && isExpandableGridModel(model) && model.hasExpandableRows) {
      this.drawCellRowTreeMarker(context, state, row);
    }
  }

  /**
   * Gets the token boxes that are visible in the cell
   * @param column The visible column
   * @param row The visible row
   * @param state The GridRenderState
   * @returns An array of TokenBox of visible tokens or empty array with coordinates relative to gridX and gridY
   */
  getTokenBoxesForVisibleCell(column, row, state) {
    var metrics = state.metrics,
      context = state.context,
      model = state.model,
      theme = state.theme;
    if (context == null || metrics == null) {
      return EMPTY_ARRAY;
    }
    var modelRows = metrics.modelRows,
      modelColumns = metrics.modelColumns;
    var modelRow = getOrThrow(modelRows, row);
    var modelColumn = getOrThrow(modelColumns, column);
    var text = model.textForCell(modelColumn, modelRow);
    var _GridUtils$getTextRen2 = GridUtils.getTextRenderMetrics(state, column, row),
      textWidth = _GridUtils$getTextRen2.width,
      textX = _GridUtils$getTextRen2.x,
      textY = _GridUtils$getTextRen2.y;
    var fontWidthsLower = metrics.fontWidthsLower,
      fontWidthsUpper = metrics.fontWidthsUpper;

    // Set the font and baseline and change it back after
    context.save();
    this.configureContext(context, state);
    var fontWidthLower = fontWidthsLower.get(context.font);
    var fontWidthUpper = fontWidthsUpper.get(context.font);
    var truncationChar = model.truncationCharForCell(modelColumn, modelRow);
    var truncatedText = this.getCachedTruncatedString(context, text, textWidth, fontWidthLower, fontWidthUpper, truncationChar);
    var _context$measureText3 = context.measureText(truncatedText),
      actualBoundingBoxAscent = _context$measureText3.actualBoundingBoxAscent,
      actualBoundingBoxDescent = _context$measureText3.actualBoundingBoxDescent;
    var textHeight = actualBoundingBoxAscent + actualBoundingBoxDescent;
    var tokens = model.tokensForCell(modelColumn, modelRow, truncatedText.length);

    // Check if the truncated text contains a link
    if (tokens.length === 0) {
      context.restore();
      return EMPTY_ARRAY;
    }
    var cachedTokenBoxes = this.getCachedTokenBoxesForVisibleCell(truncatedText, tokens, theme.font, 'middle', textHeight, context).map(tokenBox => ({
      x1: tokenBox.x1 + textX,
      y1: tokenBox.y1 + (textY - actualBoundingBoxAscent),
      x2: tokenBox.x2 + textX,
      y2: tokenBox.y2 + (textY - actualBoundingBoxAscent),
      token: tokenBox.token
    }));
    context.restore();
    return cachedTokenBoxes;
  }
  configureContext(context, state) {
    var theme = state.theme;
    context.font = theme.font;
    context.textBaseline = 'middle';
    context.lineCap = 'butt';
  }
}
export default TextCellRenderer;
//# sourceMappingURL=TextCellRenderer.js.map